<script setup lang="ts">
import { computed, ref } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppStandardModal } from '@libs/ui';
import type {
  AutoCompleteSuggestion,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import { exhaustiveGuard } from '@libs/utils';

import type { EditTripSegment } from './models/editTripSegment';
import EditTripView from './EditTripView.vue';
import type { EditTripStep } from './models/editTripSteps';
import { EditTripStepType } from './models/editTripSteps';

interface EditTripModalProps {
  segments: EditTripSegment[];
  isDesktop: boolean;
  initialStep: EditTripStep;
}
const props = defineProps<EditTripModalProps>();

interface EditTripModalEmits {
  (eventName: 'add-destination', suggestion: AutoCompleteSuggestion): void;
  (
    eventName: 'add-destination-to-own-stay',
    payload: {
      suggestion: AutoCompleteSuggestion;
      ingredientId: IngredientId;
    }
  ): void;
  (
    eventName: 'change-destination',
    payload: { suggestion: AutoCompleteSuggestion; ingredientId: IngredientId }
  ): void;
  (eventName: 'change-trip-order', segments: EditTripSegment[]): void;
  (eventName: 'change-number-of-nights', segment: EditTripSegment): void;
  (eventName: 'remove-destination', segment: EditTripSegment): void;
  (eventName: 'close'): void;
}
const emit = defineEmits<EditTripModalEmits>();

const { t } = i18n.global;

const currentStep = ref<EditTripStep>(props.initialStep);

const title = computed<string>(() => {
  switch (currentStep.value.stepType) {
    case EditTripStepType.AddDestination:
    case EditTripStepType.AddDestinationToOwnStay:
      return t('planner.add_destination');

    case EditTripStepType.UpdateDestination:
      return t('planner.change_destination');

    case EditTripStepType.Overview:
      return t('planner.edit_trip');

    default:
      return exhaustiveGuard(currentStep.value, '');
  }
});

function updateCurrentStep(newStep: EditTripStep): void {
  currentStep.value = newStep;
}

function confirm(): void {
  if (currentStep.value.isInitialStep) {
    close();
  } else {
    if (
      currentStep.value.stepType === EditTripStepType.AddDestination ||
      currentStep.value.stepType === EditTripStepType.UpdateDestination
    ) {
      updateCurrentStep({
        stepType: EditTripStepType.Overview,
      });
    } else {
      close();
    }
  }
}

function close(): void {
  emit('close');
}
</script>

<template>
  <AppStandardModal
    :close-button-aria-label="$t('a11y.close_panel')"
    @close="close"
    @confirm="confirm"
  >
    <template #title>
      {{ title }}
    </template>

    <template #body>
      <EditTripView
        :segments="props.segments"
        :is-desktop="props.isDesktop"
        :initial-step="currentStep"
        @add-destination="emit('add-destination', $event)"
        @add-destination-to-own-stay="
          emit('add-destination-to-own-stay', $event)
        "
        @change-destination="emit('change-destination', $event)"
        @change-trip-order="emit('change-trip-order', $event)"
        @change-number-of-nights="emit('change-number-of-nights', $event)"
        @remove-destination="emit('remove-destination', $event)"
        @change-step="updateCurrentStep"
        @close="close"
      />
    </template>
    <template #button>
      {{ $t('planner.done') }}
    </template>
  </AppStandardModal>
</template>

<style scoped lang="scss">
.edit-trip-modal {
  .message-box {
    margin-bottom: var(--spacing-24);
    text-align: left;
    justify-content: flex-start;
  }

  .add-destination-button {
    margin-left: 36px;
    margin-bottom: var(--spacing-24);
  }
}
</style>
